import { TFunction } from "i18next";
import { Link } from "react-router-dom";
import EuroIcon from "@mui/icons-material/Euro";
import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";
import { Autocomplete, TextField, Tooltip } from "@mui/material";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import { replaceDate } from "@/helper/dateHandling";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { arrayOfMonth, getNameOfMonth } from "@/helper/getMonthNamesByNumber";
import { getStatusLabel } from "@/helper/statusOptions";
import { KAER_COMPANY_SUBJECT_ID } from "@/helper/systemIds";

interface TrackedServiceColumnFilterOptions {
  yesNoFilterOptions?: { value: string; label: string }[];
  statusFilterOptions?: { value: string; label: string }[];
  mainCategoryOptionsForFiltering?: { value: string; label: string }[];
  trackedServiceTypeForFiltering?: { value: string; label: string }[];
  trackedServiceTemplateForFiltering?: { value: string; label: string }[];
  kaerEmployeeForFiltering?: { value: string; label: string }[];
}

interface GetTrackedServiceColumnsOptions
  extends TrackedServiceColumnFilterOptions {
  t: TFunction;
  styles: Record<string, string>;
  includeNavigationLinks?: boolean;
}

export const getTrackedServiceColumns = ({
  t,
  styles,
  includeNavigationLinks = true,
  yesNoFilterOptions = [],
  statusFilterOptions = [],
  mainCategoryOptionsForFiltering = [],
  trackedServiceTypeForFiltering = [],
  trackedServiceTemplateForFiltering = [],
  kaerEmployeeForFiltering = [],
}: GetTrackedServiceColumnsOptions): any[] => [
  { header: t("id"), accessorKey: "id", id: "id" },
  {
    header: t("checked"),
    accessorKey: "checked",
    id: "checked",
    enableSorting: false,
    filterVariant: "select",
    filterSelectOptions: yesNoFilterOptions,
    accessorFn: (row: any) => {
      if (row.checked !== null) {
        if (row.checked) {
          return (
            <span>
              <CheckIcon sx={{ color: "#008c7d" }} />
            </span>
          );
        }
        return (
          <span>
            <RemoveIcon sx={{ color: "#dc3545" }} />
          </span>
        );
      }
    },
  },
  {
    header: t("billed"),
    accessorKey: "billed",
    id: "billed",
    enableSorting: false,
    filterVariant: "select",
    filterSelectOptions: yesNoFilterOptions,
    accessorFn: (row: any) => {
      if (row.billed !== null) {
        if (row.billed) {
          return (
            <span>
              <EuroIcon sx={{ color: "#008c7d" }} />
            </span>
          );
        }
        return (
          <span>
            <RemoveIcon sx={{ color: "#dc3545" }} />
          </span>
        );
      }
    },
  },
  {
    header: t("customer_id"),
    id: "customer_id",
    accessorKey: "company_subject.customer_id",
    customColumnFilterId: "company_subject_customer_id",
    customSortingKey: "customer_id",
    sortDescFirst: false,
    setNoFilterParameter: true,
    accessorFn: (row: any) => {
      if (row.company_subject?.customer_id !== null) {
        return row.company_subject?.customer_id;
      }
      return "-";
    },
  },
  {
    header: t("company"),
    accessorKey: "company_subject",
    id: "company_subject",
    enableForCreate: true,
    required: true,
    errorMessage: t("field_required"),
    asyncEntity: "subject",
    asyncDropDown: true,
    customColumnFilterId: "company_subject_name",
    customFilterParameter: "__icontains",
    queryParameter: "companies",
    accessorFn: (row: any) => {
      if (includeNavigationLinks) {
        return (
          <NavigateToSubjectOverviewLink
            subjectId={row.company_subject.id}
            label={row.company_subject.name}
          />
        );
      }
      return row.company_subject?.name ?? "-";
    },
  },
  {
    header: t("parent"),
    id: "parent_company",
    accessorKey: "parent_company",
    enableColumnFilter: false,
    enableSorting: false,
    accessorFn: (row: any) => {
      if (includeNavigationLinks) {
        if (row.parent_company !== null) {
          return (
            <NavigateToSubjectOverviewLink
              companyId={row.parent_company.id}
              label={row.parent_company.name}
            />
          );
        }
        return (
          <NavigateToSubjectOverviewLink
            subjectId={row.company_subject.id}
            label={row.company_subject.name}
          />
        );
      }
      if (row.parent_company !== null) {
        return row.parent_company.name;
      }
      return row.company_subject?.name ?? "-";
    },
  },
  {
    header: t("person"),
    accessorKey: "subject.name",
    id: "subject",
    enableForCreate: true,
    asyncEntity: "subject",
    asyncDropDown: true,
    customColumnFilterId: "subject_name",
    customFilterParameter: "__icontains",
    accessorFn: (row: any) => {
      if (row.subject !== null) {
        if (includeNavigationLinks) {
          return (
            <NavigateToSubjectOverviewLink
              subjectId={row.subject.id}
              label={row.subject.name}
            />
          );
        }
        return row.subject.name;
      }
      return null;
    },
  },
  {
    header: t("birth_date"),
    accessorKey: "subject.person.birth_date",
    id: "birth_date",
    filterVariant: "date",
    customColumnFilterId: "subject_birth_date",
    setNoFilterParameter: true,
    filterDateRangeFormat: "date",
    enableSorting: false,
    Cell: ({ cell }: any) => {
      const value = cell.getValue();
      return <span>{value == null ? "-" : replaceDate(value)}</span>;
    },
  },
  {
    header: t("buildings"),
    accessorKey: "building.name",
    customColumnFilterId: "building__name",
    customFilterParameter: "__icontains",
    id: "building",
    accessorFn: (row: any) => {
      if (row.building !== null) {
        return row.building.name;
      }
      return "";
    },
  },
  {
    header: t("trackedservice_description_nameing"),
    accessorKey: "description",
    id: "description",
    enableForCreate: true,
    textarea: true,
  },
  {
    header: t("trackedservice_template"),
    accessorKey: "tracked_service_template",
    id: "tracked_service_template",
    entity: "trackedServiceTemplate",
    filterVariant: "multi-select",
    minSize: 350,
    filterSelectOptions: trackedServiceTemplateForFiltering,
    Filter: ({ column }: any) => {
      const filterValue = (column.getFilterValue() ?? []) as string[];
      const selectedOptions = trackedServiceTemplateForFiltering.filter((opt) =>
        filterValue.includes(opt.value)
      );

      return (
        <Autocomplete
          multiple
          fullWidth
          size="small"
          options={trackedServiceTemplateForFiltering}
          value={selectedOptions}
          renderTags={(value) => {
            if (value.length === 0) {
              return null;
            }

            if (value.length === 1) {
              return value[0].label;
            }

            const firstLabel = value[0].label;
            const remaining = value.slice(1);

            return (
              <div>
                <span>{firstLabel} </span>
                <Tooltip
                  placement="top"
                  title={
                    <div>
                      {remaining.map((opt) => (
                        <div key={opt.value}>{opt.label}</div>
                      ))}
                    </div>
                  }
                >
                  <span style={{ cursor: "pointer", color: "#8c1ec8" }}>
                    +{remaining.length}
                  </span>
                </Tooltip>
              </div>
            );
          }}
          getOptionLabel={(option) => option.label}
          onChange={(_, newOptions) => {
            column.setFilterValue(newOptions.map((opt) => opt.value));
          }}
          renderInput={(params) => <TextField {...params} variant="standard" />}
        />
      );
    },
    accessorFn: (row: any) => {
      if (row.tracked_service_template) {
        return row.tracked_service_template?.name;
      }
    },
    enableForCreate: true,
    required: true,
    errorMessage: t("field_required"),
    customColumnFilterId: "tracked_service_template_id",
    customFilterParameter: "__in",
  },
  {
    header: t("category"),
    accessorKey: "category",
    id: "category",
    setNoFilterParameter: true,
    enableForCreate: true,
    entity: "mainCategory",
    filterVariant: "select",
    filterSelectOptions: mainCategoryOptionsForFiltering,
    required: true,
    errorMessage: t("field_required"),
    Cell: ({ cell }: any) => {
      return <span>{cell.getValue().name}</span>;
    },
  },
  {
    header: t("examination"),
    acessorKey: "examination",
    customColumnFilterId: "examination__name",
    customFilterParameter: "__icontains",
    id: "examination",
    accessorFn: (row: any) => {
      if (row.examination) {
        return row.examination.name;
      }
      return "-";
    },
  },
  {
    header: t("executed_on"),
    accessorKey: "executed_on",
    id: "executed_on",
    filterVariant: "date",
    sortingFn: "date",
    accessorFn: (row: any) => {
      const appointmentId = row?.appointment?.id;
      const status = row?.appointment?.status;
      const formattedDate = replaceDate(row.executed_on);
      const statusArray = ["missed", "declined", "canceled_not_in_time"];

      if (
        includeNavigationLinks &&
        row.executed_on !== null &&
        row.executed_on !== undefined &&
        appointmentId
      ) {
        let navigateTo = `/appointmentDashboard/${appointmentId}`;
        let linkStyle = styles.link;
        if (statusArray.includes(status)) {
          navigateTo = `/settings/appointmentEditor/${appointmentId}`;
          linkStyle = styles.linkSecondary;
        }

        return (
          <span>
            <Link
              target="_blank"
              rel="noopener noreferrer"
              className={linkStyle}
              to={navigateTo}
            >
              {formattedDate}
            </Link>
          </span>
        );
      }

      return <span>{formattedDate}</span>;
    },
  },
  {
    header: t("status"),
    id: "status",
    accessorKey: "appointment.status",
    filterVariant: "select",
    filterSelectOptions: statusFilterOptions,
    customColumnFilterId: "appointment__status",
    customFilterParameter: "__icontains",
    enableSorting: false,
    accessorFn: (row: any) => {
      if (row.appointment?.status) {
        return getStatusLabel(row.appointment?.status);
      }
      return "-";
    },
  },
  {
    header: t("executed_by"),
    id: "executed_by",
    customColumnFilterId: "executed_by_name",
    accessorKey: "executed_by",
    getOnlyActiveUsers: {
      status: true,
      companySubjectId: KAER_COMPANY_SUBJECT_ID,
    },
    accessorFn: (row: any) => {
      if (row.executed_by && row.executed_by !== null) {
        return row.executed_by.name;
      }
      return "-";
    },
  },
  {
    header: t("assisted_by"),
    id: "assisted_by",
    customColumnFilterId: "assisted_by_name",
    accessorKey: "assisted_by",
    getOnlyActiveUsers: {
      status: true,
      companySubjectId: KAER_COMPANY_SUBJECT_ID,
    },
    accessorFn: (row: any) => {
      if (row.assisted_by && row.assisted_by !== null) {
        return row.assisted_by.name;
      }
      return "-";
    },
  },
  {
    header: t("month"),
    id: "month",
    accessorKey: "month",
    setNoFilterParameter: true,
    filterVariant: "select",
    filterSelectOptions: arrayOfMonth,
    customSortingKey: "executed_on",
    accessorFn: (row: any) => {
      const numberOfMonth = row.month;
      if (numberOfMonth === null || numberOfMonth === undefined) {
        return "-";
      }
      return getNameOfMonth(numberOfMonth);
    },
  },
  {
    header: t("trackedservice_estimated_duration_short"),
    accessorKey: "estimated_duration",
    id: "estimated_duration",
    setNoFilterParameter: true,
    enableForCreate: true,
    textfieldType: "number",
    required: true,
    errorMessage: t("field_required"),
  },
  {
    header: t("trackedservice_actual_duration_short"),
    accessorKey: "actual_duration",
    setNoFilterParameter: true,
    id: "actual_duration",
    enableForCreate: true,
    textfieldType: "number",
    required: true,
    errorMessage: t("field_required"),
  },
  {
    header: t("price_net"),
    accessorKey: "price",
    id: "price",
    filterVariant: "price",
    customColumnFilterId: "price",
    customFilterParameter: "__icontains",
    setNoFilterParameter: true,
    enableForCreate: true,
    textfieldType: "number",
    required: true,
    errorMessage: t("field_required"),
    Cell: ({ cell }: any) => {
      return <span>{cell.getValue()} €</span>;
    },
  },
  {
    header: t("vat_short"),
    id: "vat",
    accessorKey: "vat",
    textfieldType: "number",
    customColumnFilterId: "vat",
    setNoFilterParameter: true,
    allowZeroAsValue: true,
    enableForCreate: true,
    Cell: ({ cell }: any) => {
      const value = `${cell.getValue()}%`;
      return <span>{value}</span>;
    },
  },
  {
    header: t("trackedservice_type"),
    accessorKey: "tracked_service_type",
    id: "tracked_service_type",
    filterVariant: "select",
    setNoFilterParameter: true,
    filterSelectOptions: trackedServiceTypeForFiltering,
    enableForCreate: true,
    entity: "trackedServiceType",
    required: true,
    errorMessage: t("field_required"),
    Cell: ({ cell }: any) => {
      return <span>{cell.getValue().name}</span>;
    },
  },
  {
    header: t("trackedservice_info_ops"),
    accessorKey: "info_ops",
    id: "info_ops",
    enableForCreate: true,
    textarea: true,
    customSortingKey: "info_ops",
    sortDescFirst: false,
  },
  {
    header: t("trackedservice_info_finance"),
    accessorKey: "info_finance",
    id: "info_finance",
    enableForCreate: true,
    textarea: true,
    customSortingKey: "info_finance",
    sortDescFirst: false,
  },
  {
    header: t("trackedservice_checked_on"),
    accessorKey: "checked_on",
    id: "checked_on",
    filterVariant: "date",
    sortingFn: "date",
    Cell: ({ cell }: any) => {
      return <span>{replaceDate(cell.getValue())}</span>;
    },
  },
  {
    header: t("trackedservice_checked_by"),
    accessorKey: "checked_by",
    customColumnFilterId: "checked_by",
    id: "checked_by",
    entity: "user",
    filterVariant: "select",
    filterSelectOptions: kaerEmployeeForFiltering,
    enableSorting: false,
    enableForCreate: true,
    getOnlyActiveUsers: {
      status: true,
      companySubjectId: KAER_COMPANY_SUBJECT_ID,
    },
    accessorFn: (row: any) => {
      if (row.checked_by && row.checked_by !== null) {
        return row.checked_by.name;
      }
      return "-";
    },
  },
  {
    header: t("trackedservice_billed_on"),
    accessorKey: "billed_on",
    id: "billed_on",
    filterVariant: "date",
    sortingFn: "date",
    Cell: ({ cell }: any) => {
      return <span>{replaceDate(cell.getValue())}</span>;
    },
  },
  {
    header: t("trackedservice_billed_by"),
    accessorKey: "billed_by",
    id: "billed_by",
    customColumnFilterId: "billed_by",
    enableForCreate: true,
    entity: "user",
    filterVariant: "select",
    filterSelectOptions: kaerEmployeeForFiltering,
    getOnlyActiveUsers: {
      status: true,
      companySubjectId: KAER_COMPANY_SUBJECT_ID,
    },
    accessorFn: (row: any) => {
      if (row.billed_by && row.billed_by !== null) {
        return row.billed_by.name;
      }
      return "-";
    },
  },
  {
    header: t("created_on"),
    accessorKey: "created_on",
    filterVariant: "date",
    sortingFn: "date",
    id: "created_on",
    Cell: ({ cell }: any) => {
      return <span>{replaceDate(cell.getValue())}</span>;
    },
  },
  {
    header: t("trackedservice_on_site"),
    accessorKey: "on_site",
    id: "on_site",
    enableForCreate: true,
    enableSorting: false,
    muiFilterCheckboxProps: {
      size: "small",
    },
    checkBox: true,
    filterVariant: "checkbox",
    accessorFn: (row: any) => {
      if (row.on_site !== null) {
        if (row.on_site) {
          return t("yes");
        }
        return t("no");
      }
      return "-";
    },
  },
  ...getRecordMetadataColumns(t, ["created_by", "modified_on", "modified_by"]),
];
